import React from 'react';
import {
  Filter,
  Upload,
  Plus,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { WeekNavigator } from '../../components/WeekNavigator';

interface OverviewDashboardHeaderProps {
  weekStart: Date;
  weekEnd: Date;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  currentWeek: Date;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onCurrentWeek: () => void;
  onWeekSelect: (weekStartYmd: string) => void;
  onExport: () => void;
  onRefresh: () => void;
  refreshing?: boolean;
  loading?: boolean;
  onCustomReport?: () => void;
}

const OverviewDashboardHeader: React.FC<OverviewDashboardHeaderProps> = ({
  weekStart,
  weekEnd,
  weekStartsOn,
  currentWeek,
  onPreviousWeek,
  onNextWeek,
  onCurrentWeek,
  onWeekSelect,
  onExport,
  onRefresh,
  refreshing = false,
  loading = false,
  onCustomReport,
}) => {
  const { employee } = useAuth();
  const firstName = employee?.firstName ?? 'there';

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Overview Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">Welcome back, {firstName} 👋</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <WeekNavigator
          weekStart={weekStart}
          weekEnd={weekEnd}
          weekStartsOn={weekStartsOn}
          currentWeek={currentWeek}
          onPreviousWeek={onPreviousWeek}
          onNextWeek={onNextWeek}
          onCurrentWeek={onCurrentWeek}
          onWeekSelect={onWeekSelect}
          disabled={loading}
          variant="subtle"
          showCurrentWeekButton={false}
          dateFormat="long"
          className="flex-shrink-0"
        />

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          title="Refresh data"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>

        <button
          type="button"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          title="Use the location selector in the top navigation"
        >
          <Filter className="h-4 w-4 text-gray-400" />
          Filters
        </button>

        <button
          type="button"
          onClick={onExport}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Upload className="h-4 w-4 text-gray-400" />
          Export
        </button>

        <button
          type="button"
          onClick={onCustomReport}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Custom Report
        </button>
      </div>
    </div>
  );
};

export default OverviewDashboardHeader;
